'use client';

/**
 * Revoice Studio — re-voice any talking-head video with a new script.
 * Upload video → (optional trim) → write script → pick voice (incl. cloned)
 * → generate. Can also clone a new voice from the uploaded video's audio.
 */

import { useEffect, useRef, useState } from 'react';
import { Mic, Film, Wand2, Copy, Loader2 } from 'lucide-react';

interface Voice { id: string; name: string; category: string }

export default function RevoicePage() {
  const [voices, setVoices] = useState<Voice[]>([]);
  const [voiceId, setVoiceId] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [script, setScript] = useState('');
  const [trimStart, setTrimStart] = useState('');
  const [trimEnd, setTrimEnd] = useState('');
  const [job, setJob] = useState<string | null>(null);
  const [status, setStatus] = useState<any>(null);
  const [cloning, setCloning] = useState(false);
  const [cloneName, setCloneName] = useState('');
  const [error, setError] = useState('');
  const fileRef = useRef<HTMLInputElement>(null);

  const loadVoices = () =>
    fetch('/api/content/revoice').then(r => r.json()).then(d => {
      setVoices(d.voices || []);
      if (d.voices?.length && !voiceId) setVoiceId(d.voices[0].id);
    }).catch(() => {});

  useEffect(() => { loadVoices(); }, []); // eslint-disable-line

  // poll job
  useEffect(() => {
    if (!job) return;
    const t = setInterval(async () => {
      const s = await fetch(`/api/content/revoice?job=${job}`).then(r => r.json()).catch(() => null);
      if (s) setStatus(s);
      if (s?.status === 'done' || s?.status === 'failed') clearInterval(t);
    }, 3000);
    return () => clearInterval(t);
  }, [job]);

  const generate = async () => {
    if (!file || !script.trim() || !voiceId) { setError('Video, script and voice are required.'); return; }
    setError(''); setStatus(null);
    const fd = new FormData();
    fd.append('video', file);
    fd.append('script', script);
    fd.append('voiceId', voiceId);
    if (trimStart) fd.append('trimStart', trimStart);
    if (trimEnd) fd.append('trimEnd', trimEnd);
    const res = await fetch('/api/content/revoice', { method: 'POST', body: fd }).then(r => r.json());
    if (res.job) { setJob(res.job); setStatus({ status: 'running' }); }
    else setError(res.error || 'failed to start');
  };

  const clone = async () => {
    if (!file || !cloneName.trim()) { setError('Upload the video and name the voice first.'); return; }
    setError(''); setCloning(true);
    const fd = new FormData();
    fd.append('media', file);
    fd.append('name', cloneName.trim());
    const res = await fetch('/api/content/revoice/clone', { method: 'POST', body: fd }).then(r => r.json());
    setCloning(false);
    if (res.voiceId) { await loadVoices(); setVoiceId(res.voiceId); setCloneName(''); }
    else setError(res.error || 'clone failed');
  };

  return (
    <div className="p-6 lg:p-10 max-w-5xl mx-auto space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-white flex items-center gap-3">
          <span className="w-10 h-10 rounded-2xl bg-gradient-to-br from-pink-500/30 to-cyan-500/20 flex items-center justify-center"><Mic className="w-5 h-5 text-pink-300" /></span>
          Revoice Studio
        </h1>
        <p className="text-sm text-gray-400 mt-1">New script, same face — ElevenLabs voice + lip-sync onto any talking-head video. No re-shoot.</p>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Left: inputs */}
        <div className="space-y-4">
          {/* Video */}
          <div className="glass-card rounded-2xl p-5">
            <p className="text-[11px] uppercase tracking-[0.2em] text-gray-500 mb-3 flex items-center gap-2"><Film className="w-3.5 h-3.5" /> Source video</p>
            <input ref={fileRef} type="file" accept="video/*" className="hidden"
              onChange={e => setFile(e.target.files?.[0] || null)} />
            <button onClick={() => fileRef.current?.click()}
              className="w-full rounded-xl border border-dashed border-white/15 hover:border-cyan-500/40 py-8 text-sm text-gray-400 hover:text-cyan-300 transition-colors">
              {file ? <span className="text-cyan-300">{file.name} · {(file.size / 1e6).toFixed(1)} MB</span> : 'Click to choose a talking-head video'}
            </button>
            <div className="flex gap-3 mt-3">
              <input value={trimStart} onChange={e => setTrimStart(e.target.value)} placeholder="Trim from (e.g. 0:12)"
                className="flex-1 rounded-lg border border-white/[0.08] bg-white/[0.05] px-3 py-2 text-sm text-white placeholder:text-gray-600" />
              <input value={trimEnd} onChange={e => setTrimEnd(e.target.value)} placeholder="Trim to (e.g. 2:30)"
                className="flex-1 rounded-lg border border-white/[0.08] bg-white/[0.05] px-3 py-2 text-sm text-white placeholder:text-gray-600" />
            </div>
          </div>

          {/* Script */}
          <div className="glass-card rounded-2xl p-5">
            <p className="text-[11px] uppercase tracking-[0.2em] text-gray-500 mb-3">New script</p>
            <textarea value={script} onChange={e => setScript(e.target.value)} rows={7}
              placeholder="What should they say instead?"
              className="w-full rounded-xl border border-white/[0.08] bg-white/[0.05] px-3 py-2.5 text-sm text-white placeholder:text-gray-600 resize-y" />
          </div>

          {/* Voice */}
          <div className="glass-card rounded-2xl p-5 space-y-3">
            <p className="text-[11px] uppercase tracking-[0.2em] text-gray-500">Voice</p>
            <select value={voiceId} onChange={e => setVoiceId(e.target.value)}
              className="w-full rounded-lg border border-white/[0.08] bg-[#0d1117]/85 px-3 py-2 text-sm text-white">
              {voices.map(v => (
                <option key={v.id} value={v.id}>{v.category === 'cloned' ? '★ ' : ''}{v.name}{v.category === 'cloned' ? ' (cloned)' : ''}</option>
              ))}
            </select>
            <div className="flex gap-2 items-center pt-1 border-t border-white/[0.04]">
              <Copy className="w-3.5 h-3.5 text-gray-500 flex-shrink-0" />
              <input value={cloneName} onChange={e => setCloneName(e.target.value)} placeholder="Clone voice from this video — name it (e.g. Olga)"
                className="flex-1 rounded-lg border border-white/[0.08] bg-white/[0.05] px-3 py-2 text-sm text-white placeholder:text-gray-600" />
              <button onClick={clone} disabled={cloning}
                className="rounded-lg px-3 py-2 text-sm bg-pink-500/15 text-pink-300 border border-pink-500/25 hover:bg-pink-500/25 transition-colors disabled:opacity-50">
                {cloning ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Clone'}
              </button>
            </div>
          </div>

          <button onClick={generate} disabled={status?.status === 'running'}
            className="w-full rounded-2xl py-3.5 font-semibold text-sm bg-gradient-to-r from-cyan-500/25 to-blue-500/20 text-cyan-200 border border-cyan-500/30 hover:from-cyan-500/35 transition-all shadow-[0_0_24px_rgba(0,240,255,0.12)] disabled:opacity-50 flex items-center justify-center gap-2">
            {status?.status === 'running' ? <><Loader2 className="w-4 h-4 animate-spin" /> Generating…</> : <><Wand2 className="w-4 h-4" /> Generate revoiced video</>}
          </button>
          {error && <p className="text-sm text-red-400">{error}</p>}
        </div>

        {/* Right: result */}
        <div className="glass-card rounded-2xl p-5 min-h-[300px] flex flex-col">
          <p className="text-[11px] uppercase tracking-[0.2em] text-gray-500 mb-3">Result</p>
          {!status && <div className="flex-1 flex items-center justify-center text-sm text-gray-600">The revoiced video appears here.</div>}
          {status?.status === 'running' && (
            <div className="flex-1 flex flex-col items-center justify-center gap-3 text-gray-400">
              <Loader2 className="w-8 h-8 animate-spin text-cyan-400" />
              <p className="text-sm">Voice + lip-sync in progress… ~1 min per 15s of video.</p>
            </div>
          )}
          {status?.status === 'failed' && (
            <pre className="flex-1 text-xs text-red-400 whitespace-pre-wrap overflow-auto">{status.log || 'failed'}</pre>
          )}
          {status?.status === 'done' && (
            <div className="space-y-3">
              <video src={status.output} controls className="w-full rounded-xl border border-white/[0.08]" />
              <a href={status.output} download="revoiced.mp4" className="inline-block text-sm text-cyan-300 hover:underline">Download</a>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
